import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiFetch } from "../lib/api";
import { clearSession, getStoredSession, saveSession } from "../lib/session";
import type { Partner, Role, User } from "../types/api";

interface AuthState {
  user: User | null;
  partner: Partner | null;
  role: Role | null;
  ready: boolean;
  login: (role: Role, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [ready, setReady] = useState(false);

  const refreshProfile = useCallback(async () => {
    const data = await apiFetch<{ user: User; partner?: Partner }>("/auth/me");
    setUser(data.user);
    setPartner(data.partner ?? null);
  }, []);

  useEffect(() => {
    if (!getStoredSession()) {
      setReady(true);
      return;
    }
    refreshProfile()
      .catch(() => clearSession())
      .finally(() => setReady(true));
  }, [refreshProfile]);

  useEffect(() => {
    const clearUser = () => {
      setUser(null);
      setPartner(null);
    };
    window.addEventListener("conecta:session-cleared", clearUser);
    return () =>
      window.removeEventListener("conecta:session-cleared", clearUser);
  }, []);

  const login = useCallback(
    async (role: Role, email: string, password: string) => {
      const endpoint =
        role === "ADMIN" ? "/auth/admin/login" : "/auth/partners/login";
      const session = await apiFetch<{
        accessToken: string;
        refreshToken: string;
      }>(endpoint, {
        method: "POST",
        body: { email, password },
        skipRefresh: true,
      });
      saveSession({ ...session, role });
      await refreshProfile();
    },
    [refreshProfile],
  );

  const logout = useCallback(async () => {
    const session = getStoredSession();
    if (session) {
      await apiFetch<void>("/auth/logout", {
        method: "POST",
        body: { refreshToken: session.refreshToken },
        skipRefresh: true,
      }).catch(() => undefined);
    }
    clearSession();
    setUser(null);
    setPartner(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      partner,
      role: user?.role ?? getStoredSession()?.role ?? null,
      ready,
      login,
      logout,
      refreshProfile,
    }),
    [user, partner, ready, login, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
